import React, { FormEventHandler, useCallback, useEffect, useMemo } from 'react';
import { useAppSelector } from '@/shared/hooks/store';
import { selectRegisterData } from '@/store/slices/onboarding/selectors';
import AboutYouImg from '@/assets/images/about-you-questions.png';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Button, Input, Alert, Select, Box, DatePicker } from '@/shared/ui';
import dayjs from '@/shared/helpers/dayjs';
import { SplitLayout } from '@/modules/onboarding/components/split-layout';
import { ICustomersUserProfileRequest } from '@/shared/endpoints/customers';
import { GENDERS } from '../../../constants';
import { useFormikWrapper } from '@/shared/hooks/formikWrapper';

export type AboutYouFormData = Partial<ICustomersUserProfileRequest>;
interface BasicInfoFormProps {
    onFormSubmit: (values: AboutYouFormData) => void;
    defaultValues?: AboutYouFormData;
    loading?: boolean;
}

interface BasicInfoProps {
    formArttributes: BasicInfoFormProps;
}

const validationSchema = Yup.object({
    firstName: Yup.string()
        .trim()
        .required('Please complete all fields to proceed.')
        .matches(/^[A-Za-z\s-]+$/, 'First name can only contain letters, spaces, and hyphens')
        .test('no-end-space', 'First name cannot end with a space', value => !value?.endsWith(' ')),
    lastName: Yup.string()
        .trim()
        .required('Please complete all fields to proceed.')
        .matches(/^[A-Za-z\s-]+$/, 'Last name can only contain letters, spaces, and hyphens')
        .test('no-end-space', 'Last name cannot end with a space', value => !value?.endsWith(' ')),
    dob: Yup.string()
        .required('Please complete all fields to proceed.')
        .test('is-18', 'You must be at least 18 years old to use MedBox.', value => {
            const birthDate = dayjs(value);
            return birthDate.add(18, 'years').isBefore(dayjs());
        }),
    gender: Yup.string().required('Please complete all fields to proceed.')
});

export const BasicInfoForm: React.FC<BasicInfoFormProps> = ({ onFormSubmit, defaultValues, loading }) => {
    const registerData = useAppSelector(selectRegisterData);
    const initialValues = useMemo(() => {
        const values = {
            firstName: '',
            lastName: '',
            gender: '',
            ...defaultValues,
            dob: defaultValues?.dob ? dayjs(defaultValues.dob) : null
        };
        if (registerData.firstName) values.firstName = registerData.firstName;
        if (registerData.lastName) values.lastName = registerData.lastName;

        return values;
    }, [defaultValues, registerData]);

    const [errorMsg, setErrorMsg] = React.useState('');

    const formik = useFormikWrapper({
        initialValues,
        validationSchema,
        onSubmit: ({ firstName, lastName, dob, gender }) => {
            const formateDob = dayjs(dob).format('YYYY-MM-DD');
            const values = {
                firstName,
                lastName,
                dob: formateDob,
                gender
            };
            onFormSubmit?.(values);
        }
    });

    const toucheds = useMemo(() => Object.values(formik.touched).some(touched => touched), [formik.touched]);

    const handleSubmit: FormEventHandler<HTMLDivElement> = useCallback(
        async e => {
            e.preventDefault();
            formik.handleSubmit();
        },
        [formik]
    );

    useEffect(() => {
        const { firstName: firstNameError, lastName: lastNameError, dob: dobError, gender: genderError } = formik.errors;
        const { firstName, lastName, dob, gender } = formik.values;
        if (!firstName || !lastName || !dob || !gender) {
            return setErrorMsg('Please complete all fields to proceed.');
        }
        if (firstNameError || lastNameError || dobError || genderError) {
            return setErrorMsg(firstNameError || lastNameError || dobError || genderError || '');
        }
        setErrorMsg('');
    }, [formik, formik.errors]);

    return (
        <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}
            bgWhite={true}
            padding="0"
            paddingLg="0"
            wrapperClassName="about-you-form"
            data-testid="about-you-basic-form"
        >
            <Input
                className="about_you__first_name"
                label="Legal First Name"
                name="firstName"
                type="text"
                placeholder="ex. Jane"
                value={formik.values.firstName}
                onChange={formik.handleChange}
                error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                required
                testId="about-you-basic-first-name"
            />
            <Input
                className="about_you__last_name"
                label="Legal Last Name"
                name="lastName"
                type="text"
                placeholder="ex. Smith"
                value={formik.values.lastName}
                onChange={formik.handleChange}
                error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                required
                testId="about-you-basic-last-name"
            />
            <div className="flex w-full flex-row flex-nowrap items-start justify-between mini:flex-col">
                <DatePicker
                    label="Date of Birth"
                    name="dob"
                    required
                    onChange={dob => {
                        formik.setFieldValue('dob', dob, true);
                    }}
                    wrapperClassName="flex-1 w-1/2 mr-[18px] mini:mb-[1.56rem] mini:w-full"
                    value={formik.values.dob}
                    error={formik.touched.dob && Boolean(formik.errors.dob)}
                    testId="about-you-basic-dob"
                />
                <Select
                    className="about_you__gender"
                    label="Sex"
                    name="gender"
                    value={formik.values.gender}
                    placeholder="Select"
                    onChange={value => {
                        formik.setFieldValue('gender', value, true);
                    }}
                    options={GENDERS}
                    required
                    error={formik.touched.gender && Boolean(formik.errors.gender)}
                    wrapperClassName="w-1/2  mini:w-full"
                    testId="about-you-basic-gender"
                />
            </div>
            {formik.dirty && !formik.isValid && errorMsg && toucheds && <Alert testId="about-you-basic-error-message" label={errorMsg} />}
            <div className="about-you-submit-wrap">
                <Button testId="button-basic-info-continue" className="abour_you__save_btn" type="submit" variant="contained" color="primary" showEndIcon loading={loading}>
                    Save & Continue
                </Button>
            </div>
        </Box>
    );
};

export const MainTitleSection: React.FC = () => {
    const _aboutTxt = 'About Yourself';
    return (
        <div className="flex flex-col">
            <img
                src={AboutYouImg}
                alt="About you image"
                className="about-you-img block h-auto w-[7rem] self-center xl-min:hidden desktop:w-[15rem]"
            />
            <h1 className="aboutYouBasicInfoMainTitle tablet:aboutYouBasicInfoMainTitle__tablet desktop-min:aboutYouBasicInfoMainTitle__desktop__min desktop:aboutYouBasicInfoMainTitle__desktop fullhd:aboutYouBasicInfoMainTitle__fullhd">
                Let’s begin <span className="hidden md:inline">your MedBox</span> by you telling us a little more{' '}
                <span className="text-mint-green">{_aboutTxt}</span>
            </h1>
            <img
                src={AboutYouImg}
                alt="About you image"
                className="about-you-img-lg relative hidden h-auto xl-min:block desktop-min:-left-[6rem] desktop:self-center fullhd:self-center"
            />
        </div>
    );
};

export const BasicInfoComponent: React.FC<BasicInfoProps> = props => {
    return (
        <SplitLayout
            sideContent={<MainTitleSection />}
            mainContent={
                <div className="box-wrapper desktop-min:box-wrapper-lg box-border flex flex-1 flex-col items-center justify-start bg-white">
                    <BasicInfoForm {...props.formArttributes} />
                </div>
            }
        />
    );
};

export default BasicInfoComponent;
